import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Input, Button, type InputRef } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { AppDispatch, RootState } from "../../apis/store";
import { ToSearch } from "../../apis/actions/search.actions";
import {
  toGetProductCategoriesTree,
  toGetServiceCategoriesTree,
} from "../../apis/actions/vehicle.actions";
import {
  normalizeServiceTree,
  type ServiceTreeNode,
} from "../../utils/serviceCategoryTree";
import { menuData } from "./MenuData";

interface Props {
  open?: boolean;
  setOpen?: (v: boolean) => void;
  type?: "internal" | "external";
  /** Light internal header (dark icons / contrast). */
  isInternalTop?: boolean;
}

/**
 * Desktop: primary nav links + expandable search.
 * Mobile: this block is hidden by template CSS; use `MobileMenu` (burger).
 */
const Menu = ({ type, isInternalTop }: Props) => {
  const [cookies] = useCookies(["apiKey"]);
  const cookiesToken = cookies.apiKey;
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { serviceCategoryTree, productCategoryTree } = useSelector(
    (state: RootState) => state.vehicle
  );
  const [openCatalogMenu, setOpenCatalogMenu] = useState<
    "services" | "products" | null
  >(null);
  const servicesMenuRef = useRef<HTMLLIElement>(null);
  const productsMenuRef = useRef<HTMLLIElement>(null);
  const catalogCloseTimer = useRef<number>();

  const visibleItems = cookiesToken
    ? menuData
    : menuData.filter((x) => !x.private);

  const serviceTree = useMemo(
    () => normalizeServiceTree(serviceCategoryTree?.list),
    [serviceCategoryTree?.list]
  );
  const productTree = useMemo(
    () => normalizeServiceTree(productCategoryTree?.list),
    [productCategoryTree?.list]
  );

  const runSearch = useCallback(() => {
    const value = query.trim();
    if (!value) return;
    navigate("/search", { state: { name: value } });
    dispatch(ToSearch({ name: value, lang: i18n.language }));
    setExpanded(false);
  }, [query, navigate, dispatch, i18n.language]);

  useEffect(() => {
    if (!expanded) return;
    const tmr = window.setTimeout(() => {
      inputRef.current?.focus?.({ cursor: "end" });
    }, 50);
    return () => clearTimeout(tmr);
  }, [expanded]);

  useEffect(() => {
    dispatch(toGetServiceCategoriesTree({ lang: i18n.language }));
    dispatch(toGetProductCategoriesTree({ lang: i18n.language }));
  }, [dispatch, i18n.language]);

  useEffect(
    () => () => {
      if (catalogCloseTimer.current !== undefined) {
        window.clearTimeout(catalogCloseTimer.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!openCatalogMenu) return;

    const onDoc = (e: MouseEvent) => {
      const activeRef =
        openCatalogMenu === "services" ? servicesMenuRef : productsMenuRef;
      if (activeRef.current?.contains(e.target as Node)) return;
      setOpenCatalogMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenCatalogMenu(null);
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [openCatalogMenu]);

  const openCatalogWithFilter = useCallback(
    (pathname: "/services" | "/products", categoryId: number | null) => {
      const search = categoryId == null ? "" : `?cat=${categoryId}`;
      navigate({ pathname, search });
      setOpenCatalogMenu(null);
      setExpanded(false);
    },
    [navigate]
  );

  const showCatalogMenu = useCallback((key: "services" | "products") => {
    if (catalogCloseTimer.current !== undefined) {
      window.clearTimeout(catalogCloseTimer.current);
      catalogCloseTimer.current = undefined;
    }
    setOpenCatalogMenu(key);
  }, []);

  const hideCatalogMenuSoon = useCallback(() => {
    if (catalogCloseTimer.current !== undefined) {
      window.clearTimeout(catalogCloseTimer.current);
    }
    catalogCloseTimer.current = window.setTimeout(() => {
      setOpenCatalogMenu(null);
      catalogCloseTimer.current = undefined;
    }, 140);
  }, []);

  const renderCategoryTree = useCallback(
    (
      nodes: ServiceTreeNode[],
      pathname: "/services" | "/products",
      level = 0
    ) => (
      <ul
        className={`ac-services-tree ac-services-tree--level-${level}`}
        role="menu"
      >
        {nodes.map((node) => {
          const hasChildren = node.children.length > 0;

          return (
            <li
              key={node.id}
              className={`ac-services-tree__item${hasChildren ? " has-children" : ""}`}
              role="none"
            >
              <div className="ac-services-tree__row">
                <button
                  type="button"
                  className="ac-services-tree__link"
                  onClick={() => openCatalogWithFilter(pathname, node.id)}
                  role="menuitem"
                >
                  {node.name}
                  {hasChildren ? (
                    <span
                      className="ac-services-tree__indicator"
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  ) : null}
                </button>
              </div>
              {hasChildren
                ? renderCategoryTree(node.children, pathname, level + 1)
                : null}
            </li>
          );
        })}
      </ul>
    ),
    [openCatalogWithFilter]
  );

  useEffect(() => {
    if (!expanded) return;
    const onDoc = (e: MouseEvent) => {
      if (searchWrapRef.current?.contains(e.target as Node)) return;
      setExpanded(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  const theme =
    type === "internal" && isInternalTop
      ? "ac-nav-search--internal-top"
      : "ac-nav-search--hero";

  return (
    <div className="header-nav">
      <nav
        className={`nav-main-menu ac-nav-toolbar${expanded ? " ac-nav-toolbar--search-expanded" : ""}`}
      >
        {!expanded ? (
          <ul
            className={
              i18n.language === "ar" ? "ac--font-arabic" : "main-menu"
            }
          >
            {visibleItems.map((el) => (
              el.key === "services" || el.key === "products" ? (() => {
                const catalogKey = el.key as "services" | "products";
                const pathname = catalogKey === "services" ? "/services" : "/products";
                const isOpen = openCatalogMenu === catalogKey;
                const treeState =
                  catalogKey === "services" ? serviceCategoryTree : productCategoryTree;
                const tree = catalogKey === "services" ? serviceTree : productTree;
                const translationPrefix =
                  catalogKey === "services" ? "servicesMenu" : "productsMenu";

                return (
                <li
                  key={el.key}
                  ref={catalogKey === "services" ? servicesMenuRef : productsMenuRef}
                  className={`ac-nav-services ${isInternalTop ? "ac--menu-black" : ""}${
                    location.pathname === pathname ? " is-active" : ""
                  }`}
                  onMouseEnter={() => showCatalogMenu(catalogKey)}
                  onMouseLeave={hideCatalogMenuSoon}
                  onFocus={() => showCatalogMenu(catalogKey)}
                  onBlur={(event) => {
                    if (
                      !event.currentTarget.contains(event.relatedTarget as Node | null)
                    ) {
                      setOpenCatalogMenu(null);
                    }
                  }}
                >
                  <NavLink
                    to={pathname}
                    className={`ac-nav__link ac-nav-services__trigger${
                      location.pathname === pathname ? " ac-nav__link--active" : ""
                    }`}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    onClick={() => {
                      setOpenCatalogMenu(null);
                      setExpanded(false);
                    }}
                  >
                    <span>{t(`menu.${catalogKey}`)}</span>
                  </NavLink>

                  {isOpen ? (
                    <div className="ac-nav-services__menu" role="menu">
                      <button
                        type="button"
                        className="ac-services-tree__all"
                        onClick={() => openCatalogWithFilter(pathname, null)}
                        role="menuitem"
                      >
                        {t(`menu.${translationPrefix}All`)}
                      </button>

                      {treeState?.status === "loading" ? (
                        <p className="ac-services-tree__state">
                          {t(`menu.${translationPrefix}Loading`)}
                        </p>
                      ) : tree.length > 0 ? (
                        renderCategoryTree(tree, pathname)
                      ) : (
                        <p className="ac-services-tree__state">
                          {t(`menu.${translationPrefix}Empty`)}
                        </p>
                      )}
                    </div>
                  ) : null}
                </li>
                );
              })() : (
                <li
                  key={el.key}
                  className={isInternalTop ? "ac--menu-black" : ""}
                >
                  <NavLink
                    to={el.link}
                    end={el.link === "/"}
                    className={({ isActive }) =>
                      ["ac-nav__link", isActive ? "ac-nav__link--active" : ""]
                        .filter(Boolean)
                        .join(" ")
                    }
                  >
                    {t(`menu.${el.key}`)}
                  </NavLink>
                </li>
              )
            ))}
          </ul>
        ) : null}

        <div
          className="ac-nav-search-wrap"
          ref={searchWrapRef}
          role="search"
        >
          <div
            className={`ac-nav-search ${theme} ${expanded ? "ac-nav-search--expanded" : ""}`}
          >
            {!expanded ? (
              <button
                type="button"
                className="ac-nav-search__toggle"
                onClick={() => setExpanded(true)}
                aria-expanded={expanded}
                aria-label={t("search.search")}
              >
                <SearchOutlined />
              </button>
            ) : (
              <div className="ac-nav-search__field">
                <Input
                  ref={inputRef}
                  allowClear
                  placeholder={t("search.search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onPressEnter={runSearch}
                  className="ac-nav-search__input"
                  prefix={
                    <SearchOutlined className="ac-nav-search__prefix-icon" />
                  }
                  suffix={
                    <span className="ac-nav-search__suffix">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        aria-label={t("search.closeSearch")}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setQuery("");
                          setExpanded(false);
                        }}
                      />
                      <Button
                        type="primary"
                        size="small"
                        className="ac-nav-search__submit"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={runSearch}
                        disabled={!query.trim()}
                      >
                        {t("search.search-word")}
                      </Button>
                    </span>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Menu;
